import { IOptimisticStore } from '../interfaces/IOptimisticStore';
import { OptimisticAction, ReconciliationStatus } from '../../types/optimistic.types';

/**
 * Zustand Optimistic Adapter
 * 
 * DESIGN RATIONALE:
 * - Bridges standard Zustand stores with universal optimistic state management.
 * - Implements Snapshot-based rollback for perfect state restoration.
 * - Manages mutation lifecycles (Pending -> Committed/Reverted).
 * - Supports Real-time Server Reconciliation (Task 4.3).
 * 
 * SNAPSHOT SAFETY:
 * Zustand stores mix data and functions in the same object. A naive
 * JSON.parse(JSON.stringify(...)) would strip every function reference,
 * bricking the store on rollback. We therefore snapshot only serializable
 * (non-function) keys and merge them back safely during rollback.
 */
export class ZustandOptimisticAdapter<TState> implements IOptimisticStore<TState> {
    private snapshots: Map<string, Partial<TState>> = new Map();
    private activeMutations: Set<string> = new Set();
    private getStoreState: () => TState;
    private setStoreState: (state: TState | Partial<TState>) => void;

    constructor(
        getStoreState: () => TState,
        setStoreState: (state: TState | Partial<TState>) => void
    ) {
        this.getStoreState = getStoreState;
        this.setStoreState = setStoreState;
    }

    /**
     * Captures a safe, data-only snapshot of the current store state.
     * 
     * IMPORTANT: Zustand state objects contain both data AND function references.
     * JSON.parse(JSON.stringify(...)) silently strips functions, which would
     * permanently destroy store methods (markAsRead, fetchNotifications, etc.)
     * if this snapshot were ever restored via rollback.
     * 
     * Instead, we extract only serializable (non-function) properties into
     * a structurally-cloned partial snapshot that is safe to merge back.
     */
    createSnapshot(): TState {
        const currentState = this.getStoreState();
        const dataOnlySnapshot: Record<string, unknown> = {};

        for (const key of Object.keys(currentState as Record<string, unknown>)) {
            const value = (currentState as Record<string, unknown>)[key];
            if (typeof value !== 'function') {
                dataOnlySnapshot[key] = JSON.parse(JSON.stringify(value));
            }
        }

        return dataOnlySnapshot as TState;
    }

    applyOptimisticUpdate<TPayload>(action: OptimisticAction<TState, TPayload>): string {
        const mutationId = this.generateMutationId();
        const snapshot = this.createSnapshot();

        this.snapshots.set(mutationId, snapshot);
        this.activeMutations.add(mutationId);

        const nextState = action.apply(this.getStoreState(), action.payload);
        this.setStoreState(nextState);

        return mutationId;
    }

    commitMutation(mutationId: string): void {
        this.snapshots.delete(mutationId);
        this.activeMutations.delete(mutationId);
    }

    /**
     * Restores the store to its pre-mutation state by merging the
     * data-only snapshot back. This preserves all function references
     * that live on the Zustand state object.
     */
    rollbackMutation(mutationId: string): void {
        const snapshot = this.snapshots.get(mutationId);
        if (snapshot) {
            // Merge data-only snapshot back — Zustand's set() performs
            // a shallow merge, so existing function references survive.
            this.setStoreState(snapshot);
        }
        this.snapshots.delete(mutationId);
        this.activeMutations.delete(mutationId);
    }

    reconcileWithServerState(serverState: TState): void {
        // If there are NO active optimistic mutations, we can safely overwrite.
        // If there ARE mutations, the latest server state is now our 'Stable Base'.
        // In a high-fidelity implementation, we would re-apply active mutations on top of serverState.

        if (this.activeMutations.size === 0) {
            this.setStoreState(serverState);
        } else {
            // For now, we update the stable snapshots to reflect the server state
            // but keep the optimistic overlay until the API returns.
            this.snapshots.forEach((_, key) => {
                this.snapshots.set(key, serverState);
            });
        }
    }

    getReconciliationStatus(mutationId?: string): ReconciliationStatus {
        const isOptimistic = mutationId ? this.activeMutations.has(mutationId) : this.activeMutations.size > 0;
        return {
            isOptimistic,
            isSyncing: isOptimistic,
            isError: false,
            retryCount: 0,
        };
    }

    private generateMutationId(): string {
        try {
            return crypto.randomUUID();
        } catch {
            return `mut_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
        }
    }
}
